<script>
  import { format } from 'svelte-i18n';
  import { Button, Card, CardBody } from '@sveltestrap/sveltestrap';

  import { CopyText } from '@client/components/copy-text';
  import { PasswordField, SwitchField, TextField } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { DomainService } from '@client/services/domain';

  import { isGoogle } from '@client/stores/domain-settings';
  import { userStore } from '@client/stores/user';

  export let settings = {};
  export let onUpdate = null;

  let mxDomain = null;

  const getMXDomain = async () => {
    mxDomain = await DomainService.getMXDomain();
  };

  let revealing = false;
  let fetchedPassword = null;

  const getRelayPassword = async () => {
    if (revealing) {
      if (fetchedPassword) {
        return fetchedPassword;
      }

      fetchedPassword = `${$format('label.LOADING')}...`;

      const response = await DomainService.getRelayPassword();

      if (response && !response?.status) {
        fetchedPassword = response;
      }
    }
  };
</script>

<div id="routing">
  <ScrollableSection id="routing-general">
    <h1 class="mb-3">{$format('label.GENERAL')}</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="loopbackonly"
          label={$format('label.LOOPBACK_ONLY')}
          tooltip={$format('label.TOOLTIP.LOOPBACK_ONLY')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.loopbackonly}
          {onUpdate}
        />

        <SwitchField
          id="o365gcchigh"
          label={$format('label.ENABLE_MICROSOFT_GCC_HIGH')}
          tooltip={$format('label.TOOLTIP.ENABLE_MICROSOFT_GCC_HIGH')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.o365gcchigh}
          {onUpdate}
        />

        <SwitchField
          id="office365enabled"
          label={$format('label.ENABLE_MICROSOFT_365')}
          tooltip={$format('label.TOOLTIP.ENABLE_MICROSOFT_365')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.office365enabled}
          {onUpdate}
        >
          <div class="mt-3">
            {#if mxDomain}
              <b>{$format('label.MX_DOMAIN')}</b>
              <p>
                {mxDomain?.mx || 'Unable to find Microsoft 365 tenant.'}
                {#if mxDomain?.mx}
                  <CopyText label="Copy" source={mxDomain?.mx} />
                {/if}
              </p>
            {:else}
              <Button kind="secondary" on:click={getMXDomain} size="sm">{$format('label.GET_MX_DOMAIN')}</Button>
            {/if}
          </div>
        </SwitchField>

        {#if settings.office365enabled || $userStore.isStaff}
          <TextField
            id="office365hostname"
            label={$format('label.MICROSOFT_365_HOSTNAME_OVERRIDE')}
            tooltip={$format('label.TOOLTIP.MICROSOFT_365_HOSTNAME_OVERRIDE')}
            bind:value={settings.office365hostname}
            {onUpdate}
            allowCopy
          />
        {/if}

        <SwitchField
          id="googleenabled"
          label={$format('label.ENABLE_GOOGLE')}
          tooltip={$format('label.TOOLTIP.ENABLE_GOOGLE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.googleenabled}
          {onUpdate}
        />

        {#if $isGoogle}
          <SwitchField
            id="newgoogleips"
            label={$format('label.ENABLE_NEW_GOOGLE_POOL')}
            tooltip={$format('label.TOOLTIP.ENABLE_NEW_GOOGLE_POOL')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.newgoogleips}
            {onUpdate}
          />
        {/if}
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="routing-smart">
    <h1 class="mb-3">{$format('label.SMART_HOST')}</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="smarthostenabled"
          label={$format('label.SMART_HOSTING')}
          tooltip={$format('label.TOOLTIP.SMART_HOSTING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.smarthostenabled}
          {onUpdate}
        />

        <TextField
          id="smarthost"
          label={$format('label.HOSTNAME')}
          tooltip={$format('label.TOOLTIP.HOSTNAME')}
          bind:value={settings.smarthost}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="smarthostport"
          label={$format('label.PORT')}
          tooltip={$format('label.TOOLTIP.PORT')}
          bind:value={settings.smarthostport}
          {onUpdate}
        />

        <TextField
          id="smarthostuser"
          label={$format('label.USER')}
          tooltip={$format('label.TOOLTIP.USER_SMART_HOST')}
          bind:value={settings.smarthostuser}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="smarthostpassword"
          label={$format('label.PASSWORD')}
          tooltip={$format('label.TOOLTIP.PASSWORD_SMART_HOST')}
          bind:value={settings.smarthostpassword}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="routing-bulk">
    <h1 class="mb-3">{$format('label.BULK_HOST')}</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="bulkhostenabled"
          label={$format('label.BULK_HOSTING')}
          tooltip={$format('label.TOOLTIP.BULK_HOSTING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.bulkhostenabled}
          {onUpdate}
        />

        <SwitchField
          id="bulkhostoutreach"
          label={$format('label.SEND_OUTREACH_EMAIL_TO_BULK_HOST')}
          tooltip={$format('label.TOOLTIP.SEND_OUTREACH_EMAIL_TO_BULK_HOST')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.bulkhostoutreach}
          {onUpdate}
        />

        <SwitchField
          id="bulkhostzendesk"
          label={$format('label.SEND_ZENDESK_EMAIL_TO_BULK_HOST')}
          tooltip={$format('label.TOOLTIP.SEND_ZENDESK_EMAIL_TO_BULK_HOST')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.bulkhostzendesk}
          {onUpdate}
        />

        <TextField
          id="bulkheaderattribute"
          label={$format('label.HEADER_TRIGGER')}
          tooltip={$format('label.TOOLTIP.HEADER_TRIGGER')}
          bind:value={settings.bulkheaderattribute}
          {onUpdate}
        />

        <TextField
          id="bulkhost"
          label={$format('label.HOST')}
          tooltip={$format('label.TOOLTIP.HOST')}
          bind:value={settings.bulkhost}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="bulkhostport"
          label={$format('label.PORT')}
          tooltip={$format('label.TOOLTIP.PORT_BULK_HOST')}
          bind:value={settings.bulkhostport}
          {onUpdate}
        />

        <TextField
          id="bulkhostuser"
          label={$format('label.USER')}
          tooltip={$format('label.TOOLTIP.USER_BULK_HOST')}
          bind:value={settings.bulkhostuser}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="bulkhostpassword"
          label={$format('label.PASSWORD')}
          tooltip={$format('label.TOOLTIP.PASSWORD_BULK_HOST')}
          bind:value={settings.bulkhostpassword}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="routing-relay">
    <h1 class="mb-3">{$format('label.RELAY')}</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="relay"
          label={$format('label.PORT')}
          tooltip={$format('label.TOOLTIP.PORT_RELAY')}
          bind:value={settings.relay}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="relayusername"
          label={$format('label.USER')}
          tooltip={$format('label.TOOLTIP.USER_RELAY')}
          bind:value={settings.relayusername}
          showActions={false}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="relaypassword"
          label={$format('label.PASSWORD')}
          tooltip={$format('label.TOOLTIP.PASSWORD_RELAY')}
          bind:value={fetchedPassword}
          showActions={false}
          bind:revealing
          reveal
          onReveal={getRelayPassword}
          allowCopy={revealing}
        />
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss" src="./routing.scss"></style>
